import { Pagesection, Tabs, TabsPanel } from '@ktag/styleguide/dist/react';
import ApiContext from 'api/ApiContext';
import { CountingCirclesRecord } from 'api/api.types';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import NationalCouncilExemptionBox from 'lib/NationalCouncilExemptionBox/NationalCouncilExemptionBox';
import PartyBarList from 'lib/PartyBarList/PartyBarList';
import useMapApi from 'lib/Map/useMapApi';
import MunicipalityView from 'lib/MunicipalityView/MunicipalityView';
import { MuniTab } from 'lib/MapSwitch/MapSwitch';
import { PartyBarData } from 'lib/PartyBar/partybar.types';

const renderListBars = (
  listsWithDetails: PartyBarData[],
  currentElectionYear: number,
) => {
  return ({ lists, results }: CountingCirclesRecord) => {
    const partyBarData: PartyBarData[] = lists.map((list) => {
      // for lists, we only get listId and results, but not list-name etc.
      // this data is retrieved from the listsWithDetails array
      const listWithDetails = listsWithDetails.find((li) => li.listId === list.id);

      return {
        title: listWithDetails?.title,
        shortTitle: listWithDetails?.shortTitle,
        votesRelative: list?.results.party_votes_percentage,
        votesAbsolute: list?.results.party_votes,
        listId: listWithDetails?.listId,
        listNumber: listWithDetails?.listNumber,
        competedInPrevElection: listWithDetails?.competedInPrevElection,
      };
    });

    return (
      <PartyBarList
        isFinal
        lists={partyBarData}
        type="votes"
        isMinimal
        currentElectionYear={currentElectionYear}
        eligibleVoters={results.eligible_voters}
        participation={results.participation}
      />
    );
  };
};

const renderPartyBars = (
  partiesWithListIds: PartyBarData[],
  currentElectionYear: number,
) => {
  return ({ lists, results }: CountingCirclesRecord) => {
    const partyBarData: PartyBarData[] = partiesWithListIds.map((party) => {
      const partyLists = lists.filter((list) => {
        return party.listIds.includes(list.id);
      });

      const sumVotes = (key: string) => {
        return partyLists.reduce((acc, list) => {
          return acc + list.results[key];
        }, 0);
      };

      return {
        title: party?.title,
        shortTitle: party?.shortTitle,
        votesRelative: sumVotes('party_votes_percentage'),
        votesAbsolute: sumVotes('party_votes'),
        listId: party?.listId,
        listNumber: party?.listNumber,
        competedInPrevElection: party?.competedInPrevElection,
      };
    });

    return (
      <PartyBarList
        isFinal
        lists={partyBarData}
        type="votes"
        isMinimal
        currentElectionYear={currentElectionYear}
        eligibleVoters={results.eligible_voters}
        participation={results.participation}
      />
    );
  };
};

interface Props {
  parties: PartyBarData[];
  lists: PartyBarData[];
  pollId: string;
  currentElectionYear: number;
  prevElectionYear: number;
  isFinal: boolean;
}

const ProportionalProportionsNrw = ({
  parties,
  lists,
  pollId,
  currentElectionYear,
  prevElectionYear,
  isFinal,
}: Props) => {
  const { getCountingCircles } = useContext(ApiContext);
  const { t } = useTranslation('wap');
  const [selectedMuniTab, setSelectedMuniTab] = useState<MuniTab>('map');

  const query = useQuery(['counting_circles', pollId], () => getCountingCircles(pollId));

  const {
    map,
    hoveredElement: hoveredMapElement,
    selectedElement: selectedMapElement,
    isLoading: isLoadingMap,
    setSelectedElement: setSelectedMapElement,
  } = useMapApi({
    year: currentElectionYear,
    records: query.data?.data?.records,
    type: 'proportional',
    selectedMuniTab,
  });

  const partiesWithListIds = parties.map((party) => {
    const listIds = lists
      .filter((list) => list.partyId === party.listId)
      .map((list) => list.listId);
    return {
      ...party,
      listIds,
    };
  });

  return (
    <Pagesection>
      <NationalCouncilExemptionBox currentElectionYear={currentElectionYear} />
      <Tabs
        noBezel
        additionalClassNames="proportionalelectiondetails__proportions-tabs"
        ariaLabel={t('proportional_tablist_proportions_level_2')}
      >
        <TabsPanel label={t('canton')} id="canton">
          <Tabs
            type="button"
            additionalClassNames="proportionalelectiondetails__proportions-subtabs"
            ariaLabel="proportional_tablist_proportions_canton_level_3"
          >
            <TabsPanel label={t('parties')} icon="usergroup" id="parties">
              <PartyBarList
                lists={parties}
                currentElectionYear={currentElectionYear}
                prevElectionYear={prevElectionYear}
                type="votes"
                isFinal={isFinal}
                isCantonalTab
              />
            </TabsPanel>
            <TabsPanel label={t('lists')} icon="chartbar" id="lists">
              <PartyBarList
                lists={lists}
                currentElectionYear={currentElectionYear}
                prevElectionYear={prevElectionYear}
                type="votes"
                isFinal={isFinal}
                isMinimal
              />
            </TabsPanel>
          </Tabs>
        </TabsPanel>
        <TabsPanel label={t('municipalities')} id="municipalities">
          <Tabs
            type="button"
            additionalClassNames="proportionalelectiondetails__proportions-subtabs"
          >
            <TabsPanel label={t('parties')} icon="usergroup" id="parties">
              <MunicipalityView
                selectedMuniTab={selectedMuniTab}
                setSelectedMuniTab={setSelectedMuniTab}
                map={map}
                query={query}
                hoveredMapElement={hoveredMapElement}
                selectedMapElement={selectedMapElement}
                isLoadingMap={isLoadingMap}
                render={renderPartyBars(partiesWithListIds, currentElectionYear)}
                setSelectedMapElement={setSelectedMapElement}
                hasThirdTablevel
              />
            </TabsPanel>
            <TabsPanel
              label={t('lists')}
              icon="chartbar"
              id="lists2"
              additionalClassNames="proportionalelectiondetails__proportions-munis"
            >
              <MunicipalityView
                selectedMuniTab={selectedMuniTab}
                setSelectedMuniTab={setSelectedMuniTab}
                map={map}
                query={query}
                hoveredMapElement={hoveredMapElement}
                selectedMapElement={selectedMapElement}
                setSelectedMapElement={setSelectedMapElement}
                isLoadingMap={isLoadingMap}
                render={renderListBars(lists, currentElectionYear)}
                hasThirdTablevel
              />
            </TabsPanel>
          </Tabs>
        </TabsPanel>
      </Tabs>
    </Pagesection>
  );
};

export default ProportionalProportionsNrw;
